/**
 * Auth store — JWT and current backoffice user.
 *
 * Persistence: `access_token` + serialized `user` in localStorage so a
 * page refresh doesn't bounce to /login. We don't try to validate the
 * token on restore — the first API call after restore will 401 if it's
 * expired, and the response interceptor kicks the user out.
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { BackofficeUser } from "@/types";

const TOKEN_KEY = "admin.access_token";
const USER_KEY = "admin.user";

export const useAuthStore = defineStore("auth", () => {
  const token = ref<string | null>(null);
  const user = ref<BackofficeUser | null>(null);

  const isAuthenticated = computed(() => token.value !== null);

  function setSession(t: string, u: BackofficeUser) {
    token.value = t;
    user.value = u;
    localStorage.setItem(TOKEN_KEY, t);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
  }

  function logout() {
    token.value = null;
    user.value = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  function restore() {
    const t = localStorage.getItem(TOKEN_KEY);
    const u = localStorage.getItem(USER_KEY);
    if (t && u) {
      token.value = t;
      try {
        user.value = JSON.parse(u);
      } catch {
        // Corrupted user blob — start fresh.
        logout();
      }
    }
  }

  return { token, user, isAuthenticated, setSession, logout, restore };
});
